/**
 * Mapeamento centralizado de planos de assinatura
 * 
 * Este arquivo contém todas as informações sobre os planos disponíveis
 * incluindo traduções, preços, limites e cores.
 * 
 * IMPORTANTE: Todos os componentes devem usar este arquivo para exibir
 * nomes de planos, garantindo consistência em toda a aplicação.
 */

// Chaves válidas de planos
export const PLAN_KEYS = ['simple', 'silver', 'gold', 'premium', 'diamond'];

// Mapeamento de nomes de planos por idioma
export const PLAN_NAMES = {
  simple: {
    'pt-BR': 'Simples',
    'en-US': 'Simple',
    pt: 'Simples',
    en: 'Simple'
  },
  silver: {
    'pt-BR': 'Prata',
    'en-US': 'Silver',
    pt: 'Prata',
    en: 'Silver'
  },
  gold: {
    'pt-BR': 'Ouro',
    'en-US': 'Gold',
    pt: 'Ouro',
    en: 'Gold'
  },
  premium: {
    'pt-BR': 'Premium',
    'en-US': 'Premium',
    pt: 'Premium',
    en: 'Premium'
  },
  diamond: {
    'pt-BR': 'Diamante',
    'en-US': 'Diamond',
    pt: 'Diamante',
    en: 'Diamond'
  }
};

// Preços mensais dos planos (em USD)
export const PLAN_PRICES = {
  simple: 9.99,
  silver: 14.99,
  gold: 19.99,
  premium: 24.99,
  diamond: 29.99
};

// Limites de fotos por plano
export const PLAN_PHOTO_LIMITS = {
  simple: 10,
  silver: 20,
  gold: 20,
  premium: 30,
  diamond: 100
};

// Cores e ícones dos planos
export const PLAN_STYLES = {
  simple: {
    color: 'bg-gray-100 text-gray-700',
    borderColor: 'border-gray-300',
    badgeColor: 'bg-gray-500',
    icon: '📋',
    gradient: 'from-gray-500 to-gray-600'
  },
  silver: {
    color: 'bg-slate-100 text-slate-700',
    borderColor: 'border-slate-400',
    badgeColor: 'bg-slate-500',
    icon: '🥈',
    gradient: 'from-slate-400 to-slate-500'
  },
  gold: {
    color: 'bg-amber-100 text-amber-700',
    borderColor: 'border-amber-400',
    badgeColor: 'bg-amber-500',
    icon: '🥇',
    gradient: 'from-amber-400 to-amber-500'
  },
  premium: {
    color: 'bg-purple-100 text-purple-700',
    borderColor: 'border-purple-400',
    badgeColor: 'bg-purple-500',
    icon: '💎',
    gradient: 'from-purple-500 to-purple-600'
  },
  diamond: {
    color: 'bg-yellow-100 text-yellow-800',
    borderColor: 'border-yellow-400',
    badgeColor: 'bg-gradient-to-r from-yellow-400 to-amber-500',
    icon: '👑',
    gradient: 'from-yellow-400 to-amber-500'
  }
};

// Recursos dos planos
export const PLAN_FEATURES = {
  simple: {
    pt: ['Até 10 fotos', 'Listagem básica', 'Suporte por email'],
    en: ['Up to 10 photos', 'Basic listing', 'Email support']
  },
  silver: {
    pt: ['Até 20 fotos', 'Destaque na busca', 'Suporte prioritário'],
    en: ['Up to 20 photos', 'Search highlight', 'Priority support']
  },
  gold: {
    pt: ['Até 20 fotos', 'WhatsApp direto', 'Website e Redes Sociais', 'Badge Gold'],
    en: ['Up to 20 photos', 'Direct WhatsApp', 'Website and Social Media', 'Gold badge']
  },
  premium: {
    pt: ['Até 30 fotos', 'Tudo do Ouro', 'Vídeo do imóvel', 'Destaque patrocinado'],
    en: ['Up to 30 photos', 'All Gold features', 'Property video', 'Sponsored highlight']
  },
  diamond: {
    pt: ['Até 100 fotos', 'Tudo do Premium', 'Carrossel Diamante na Home', 'Máximo destaque'],
    en: ['Up to 100 photos', 'All Premium features', 'Diamond carousel on Home', 'Maximum visibility']
  }
};

/**
 * Obtém o nome traduzido do plano
 * @param {string} planKey - Chave do plano (simple, silver, gold, premium, diamond)
 * @param {string} language - Idioma ('pt', 'en', 'pt-BR', 'en-US')
 * @returns {string} Nome traduzido do plano
 */
export const getPlanName = (planKey, language = 'pt') => {
  if (!planKey) return '';
  
  // Normaliza a chave do plano
  const key = planKey.toLowerCase().trim();
  
  // Trata aliases antigos
  const normalizedKey = key === 'basic' ? 'simple' : key;
  
  // Busca no mapeamento
  const planNames = PLAN_NAMES[normalizedKey];
  if (!planNames) {
    // Fallback: capitaliza a primeira letra
    return planKey.charAt(0).toUpperCase() + planKey.slice(1);
  }
  
  // Retorna o nome no idioma solicitado
  return planNames[language] || planNames['pt'] || planKey;
};

/**
 * Obtém as informações completas do plano
 * @param {string} planKey - Chave do plano
 * @param {string} language - Idioma
 * @returns {Object} Objeto com todas as informações do plano
 */
export const getPlanInfo = (planKey, language = 'pt') => {
  const key = (planKey || 'simple').toLowerCase().trim();
  const normalizedKey = key === 'basic' ? 'simple' : key;
  
  return {
    key: normalizedKey,
    name: getPlanName(normalizedKey, language),
    price: PLAN_PRICES[normalizedKey] || PLAN_PRICES.simple,
    photoLimit: PLAN_PHOTO_LIMITS[normalizedKey] || PLAN_PHOTO_LIMITS.simple,
    styles: PLAN_STYLES[normalizedKey] || PLAN_STYLES.simple,
    features: PLAN_FEATURES[normalizedKey]?.[language === 'pt' || language === 'pt-BR' ? 'pt' : 'en'] || []
  };
};

/**
 * Verifica se uma chave de plano é válida
 * @param {string} planKey - Chave do plano
 * @returns {boolean}
 */
export const isValidPlan = (planKey) => {
  if (!planKey) return false;
  const key = planKey.toLowerCase().trim();
  return PLAN_KEYS.includes(key) || key === 'basic'; // 'basic' é alias para 'simple'
};

/**
 * Normaliza uma chave de plano (converte 'basic' para 'simple')
 * @param {string} planKey - Chave do plano
 * @returns {string} Chave normalizada
 */
export const normalizePlanKey = (planKey) => {
  if (!planKey) return 'simple';
  const key = planKey.toLowerCase().trim();
  return key === 'basic' ? 'simple' : key;
};

export default {
  PLAN_KEYS,
  PLAN_NAMES,
  PLAN_PRICES,
  PLAN_PHOTO_LIMITS,
  PLAN_STYLES,
  PLAN_FEATURES,
  getPlanName,
  getPlanInfo,
  isValidPlan,
  normalizePlanKey
};
